import { useTranslation } from "react-i18next";
import type { Order } from "@/api.types.v2";
import { StatusBadge, type StatusTone } from "@/components/status-badge";

function formatCurrency(cents: number | undefined, language: string) {
  if (cents === undefined) return "—";
  return new Intl.NumberFormat(language === "zh" ? "zh-CN" : "en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function hasOrderRefund(order: Order) {
  return Boolean(
    (order.refundSummary && order.refundSummary.settlementState !== "NONE") ||
      order.paymentStatus === "REFUNDED" ||
      order.paymentStatus === "PARTIAL_REFUND"
  );
}

function OrderRefundSummary({ order }: { order: Order }) {
  const { t, i18n } = useTranslation("app");
  if (!hasOrderRefund(order)) return null;
  const summary = order.refundSummary;
  const money = (cents: number) => formatCurrency(cents, i18n.language);
  const cash = summary?.settledCashCents ?? 0;
  const credit = summary?.settledCreditCents ?? 0;
  const hasSettledRefund = cash + credit > 0;
  const isPartial =
    order.paymentStatus === "PARTIAL_REFUND" ||
    (order.totalPaidCents !== undefined &&
      cash + credit < order.totalPaidCents);
  const unsettled =
    summary &&
    ["PENDING", "PROCESSING", "FAILED"].includes(summary.settlementState);
  const outcomeTone =
    summary?.settlementState === "FAILED" ? "danger" : "warning";
  const outcomeKey = hasSettledRefund
    ? `orders.refundSummary.additional.${summary?.settlementState.toLowerCase()}`
    : `orders.refundSummary.${summary?.settlementState.toLowerCase()}`;
  return (
    <>
      {cash > 0 && (
        <StatusBadge tone="secondary" className="max-w-full whitespace-normal!">
          {t(
            isPartial
              ? "orders.refundSummary.partialCash"
              : "orders.refundSummary.cash",
            { amount: money(cash) }
          )}
        </StatusBadge>
      )}
      {credit > 0 &&
        (cash > 0 ? (
          <p className="w-full text-xs font-normal text-default-500">
            {t("orders.refundSummary.credit", { amount: money(credit) })}
          </p>
        ) : (
          <StatusBadge
            tone="secondary"
            className="max-w-full whitespace-normal!"
          >
            {t("orders.refundSummary.credit", { amount: money(credit) })}
          </StatusBadge>
        ))}
      {unsettled &&
        (hasSettledRefund ? (
          <p className="w-full text-xs font-normal text-default-500">
            {t(outcomeKey)}
          </p>
        ) : (
          <StatusBadge
            tone={outcomeTone}
            className="max-w-full whitespace-normal!"
          >
            {t(outcomeKey)}
          </StatusBadge>
        ))}
      {(!summary || summary.settlementState === "NONE") && (
        <StatusBadge tone="default" className="max-w-full whitespace-normal!">
          {t("orders.refundSummary.unavailable")}
        </StatusBadge>
      )}
      {summary?.settlementState === "COMPLETED" && !hasSettledRefund && (
        <StatusBadge tone="default" className="max-w-full whitespace-normal!">
          {t("orders.refundSummary.noCash")}
        </StatusBadge>
      )}
    </>
  );
}

const paymentDisplay: Partial<
  Record<
    NonNullable<Order["paymentStatus"]>,
    { labelKey: string; tone: StatusTone }
  >
> = {
  PAID: { labelKey: "orders.paidLabel", tone: "success" },
  CREDIT_APPROVED: {
    labelKey: "orders.paymentStatus.netTerms",
    tone: "primary",
  },
  VOIDED: { labelKey: "orders.paymentStatus.voided", tone: "default" },
  FAILED: { labelKey: "orders.paymentStatus.failed", tone: "danger" },
};

export function OrderPaymentSummary({
  order,
  showBreakdown = false,
}: {
  order: Order;
  showBreakdown?: boolean;
}) {
  const { t, i18n } = useTranslation("app");
  const showPaid =
    order.totalPaidCents !== undefined &&
    (order.totalPaidCents > 0 || order.paymentStatus === "PAID");
  const amount = showPaid ? order.totalPaidCents : order.totalCents;
  const formatted = formatCurrency(amount, i18n.language);
  const status = (order.paymentStatus &&
    paymentDisplay[order.paymentStatus]) || {
    labelKey: "orders.paymentStatus.unpaid",
    tone: "default",
  };
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-xs font-normal text-default-500">
        {t(
          showPaid
            ? "orders.refundSummary.totalPaid"
            : "orders.refundSummary.orderTotal"
        )}{" "}
        <span>{t("orders.refundSummary.currency")}</span>
      </p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold text-base text-default-900 tabular-nums">
          {formatted}
        </span>
        {hasOrderRefund(order) ? (
          <OrderRefundSummary order={order} />
        ) : (
          <StatusBadge tone={status.tone}>{t(status.labelKey)}</StatusBadge>
        )}
      </div>
      {showBreakdown &&
        showPaid &&
        order.separatePickupPaidCents !== undefined &&
        order.separatePickupPaidCents > 0 &&
        order.totalPaidCents !== undefined && (
          <div className="pt-2 space-y-1 text-xs font-normal text-default-500">
            <p className="flex justify-between gap-3">
              <span>{t("orders.refundSummary.orderPayments")}</span>
              <span>
                {formatCurrency(
                  order.totalPaidCents - order.separatePickupPaidCents,
                  i18n.language
                )}
              </span>
            </p>
            <p className="flex justify-between gap-3">
              <span>{t("orders.refundSummary.pickupPayments")}</span>
              <span>
                {formatCurrency(order.separatePickupPaidCents, i18n.language)}
              </span>
            </p>
          </div>
        )}
    </div>
  );
}
